import DiscontinueModal, {
  DeactivationMethod,
} from 'components/global/DiscontinueModal/DiscontinueModal';
import type { Dispatch, FormEvent, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import type { LineStatus } from 'redux/services/api.generated';
import {
  useBulkDeactivateSubscriptionLinesMutation,
  useBulkDiscontinueSubscriptionLinesMutation,
} from 'redux/services/api.generated';
import { formatDate } from '../../../../util/util';
import { formatISO } from 'date-fns';
import { useToast } from 'util/hooks';

interface DiscontinueSubscriptionLineModalProps {
  readonly isOpen: boolean;
  readonly setIsOpen: Dispatch<SetStateAction<boolean>>;
  readonly subscriptionLines: Array<{
    id: number;
    name: string;
    status: LineStatus;
  }>;
  readonly refetch: () => void;
  readonly datePickerMaxDate?: string;
}

function DiscontinueSubscriptionLineModal({
  isOpen,
  setIsOpen,
  subscriptionLines,
  refetch,
  datePickerMaxDate,
}: DiscontinueSubscriptionLineModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [discontinueSubscriptionLines] =
    useBulkDiscontinueSubscriptionLinesMutation();
  const [deactivateSubscriptionLines] = useBulkDeactivateSubscriptionLinesMutation();

  const handleSubmit = (
    event: FormEvent<HTMLFormElement>,
    inactiveDate: Date | null,
    closeModal: () => void,
    deactivationMethod: DeactivationMethod,
  ) => {
    event.preventDefault();
    closeModal();

    const subscriptionLineIds = subscriptionLines.map(({ id }) => id);

    if (deactivationMethod === DeactivationMethod.discontinue) {
      discontinueSubscriptionLines({
        bulkDiscontinueSubscriptionLinesCommand: {
          subscriptionLineIds,
        },
      })
        .unwrap()
        .then(() => {
          refetch();
          toast.success(t('subscriptions.productsSuccessfullyDiscontinued'));
        })
        .catch(() => {
          // Errors are handled in middleware.
        });
    }
    if (deactivationMethod === DeactivationMethod.deactivateToday) {
      deactivateSubscriptionLines({
        bulkDeactivateSubscriptionLinesCommand: {
          subscriptionLineIds,
        },
      })
        .unwrap()
        .then(() => {
          refetch();
          toast.success(t('subscriptions.productsSuccessfullyDeactivated'));
        })
        .catch(() => {
          // Errors are handled in middleware.
        });
    }
    if (deactivationMethod === DeactivationMethod.deactivateOnDate) {
      deactivateSubscriptionLines({
        bulkDeactivateSubscriptionLinesCommand: {
          subscriptionLineIds,
          inactiveDate: inactiveDate
            ? formatDate(formatISO(inactiveDate))
            : undefined,
        },
      })
        .unwrap()
        .then(() => {
          refetch();
          toast.success(t('subscriptions.productsSuccessfullyDeactivated'));
        })
        .catch(() => {
          // Errors are handled in middleware.
        });
    }
  };

  let title = t('subscriptions.discontinueSubscriptionLine.plural');
  if (subscriptionLines?.length === 1 && subscriptionLines[0].name) {
    title = `${t('subscriptions.discontinueSubscriptionLine.singular')} ${
      subscriptionLines[0].name
    }`;
  }

  return (
    <DiscontinueModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      handleSubmit={handleSubmit}
      title={title}
      disableSubmit={!subscriptionLines?.length}
      datePickerMaxDate={datePickerMaxDate}
      description={t(
        `subscriptions.whenDeactivateSubscriptionLines.${
          subscriptionLines.length === 1 ? 'singular' : 'plural'
        }`,
      )}
    />
  );
}

export default DiscontinueSubscriptionLineModal;
